import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';

const marketData = [
  { rank: 1, symbol: 'BTC', name: 'Bitcoin', icon: 'fa-brands fa-bitcoin', color: 'text-crypto-bitcoin', price: 67234.56, change24h: 2.34, change7d: 8.45, marketCap: 1320000000000, volume: 28500000000, supply: '19.6M' },
  { rank: 2, symbol: 'ETH', name: 'Ethereum', icon: 'fa-brands fa-ethereum', color: 'text-crypto-ethereum', price: 3245.78, change24h: -1.23, change7d: 5.67, marketCap: 390000000000, volume: 15200000000, supply: '120.2M' },
  { rank: 3, symbol: 'BNB', name: 'BNB', icon: 'fa-solid fa-coins', color: 'text-yellow-500', price: 567.89, change24h: 0.87, change7d: 3.21, marketCap: 87000000000, volume: 1800000000, supply: '153.8M' },
  { rank: 4, symbol: 'SOL', name: 'Solana', icon: 'fa-solid fa-sun', color: 'text-purple-500', price: 142.56, change24h: 5.67, change7d: 15.34, marketCap: 62000000000, volume: 3200000000, supply: '435.2M' },
  { rank: 5, symbol: 'XRP', name: 'XRP', icon: 'fa-solid fa-droplet', color: 'text-blue-400', price: 0.5234, change24h: -0.45, change7d: 2.11, marketCap: 28000000000, volume: 1100000000, supply: '53.5B' },
  { rank: 6, symbol: 'ADA', name: 'Cardano', icon: 'fa-solid fa-cube', color: 'text-blue-500', price: 0.4567, change24h: 1.23, change7d: -2.34, marketCap: 16000000000, volume: 450000000, supply: '35.0B' },
  { rank: 7, symbol: 'DOGE', name: 'Dogecoin', icon: 'fa-solid fa-dog', color: 'text-yellow-400', price: 0.0823, change24h: 3.45, change7d: 12.67, marketCap: 11500000000, volume: 890000000, supply: '140.0B' },
  { rank: 8, symbol: 'DOT', name: 'Polkadot', icon: 'fa-solid fa-circle-nodes', color: 'text-pink-500', price: 7.89, change24h: -2.11, change7d: 4.56, marketCap: 9800000000, volume: 320000000, supply: '1.2B' },
  { rank: 9, symbol: 'AVAX', name: 'Avalanche', icon: 'fa-solid fa-mountain', color: 'text-red-500', price: 35.67, change24h: 4.32, change7d: 9.87, marketCap: 13200000000, volume: 560000000, supply: '370.3M' },
  { rank: 10, symbol: 'LINK', name: 'Chainlink', icon: 'fa-solid fa-link', color: 'text-blue-600', price: 14.23, change24h: 1.89, change7d: 6.78, marketCap: 8300000000, volume: 410000000, supply: '583.0M' },
];

const trendingCoins = [
  { symbol: 'PEPE', name: 'Pepe', change: 45.67, icon: 'fa-solid fa-frog', color: 'text-green-500' },
  { symbol: 'WIF', name: 'dogwifhat', change: 32.45, icon: 'fa-solid fa-hat-wizard', color: 'text-pink-400' },
  { symbol: 'BONK', name: 'Bonk', change: 28.90, icon: 'fa-solid fa-bone', color: 'text-orange-500' },
  { symbol: 'FLOKI', name: 'Floki', change: 18.23, icon: 'fa-solid fa-shield-dog', color: 'text-yellow-500' },
];

const formatNumber = (num) => {
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  return `$${num.toLocaleString()}`;
};

export const Markets = () => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rank');
  const [category, setCategory] = useState('all');

  const filteredData = marketData.filter(coin => 
    coin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    coin.symbol.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalMarketCap = marketData.reduce((acc, coin) => acc + coin.marketCap, 0);
  const total24hVolume = marketData.reduce((acc, coin) => acc + coin.volume, 0);

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fade-in" data-testid="markets-page">
      {/* Page Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">{t('markets')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('trackPrices')}</p>
        </div>
      </div>

      {/* Market Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <i className="fa-solid fa-globe text-primary text-sm sm:text-base"></i>
              </div>
              <div className="min-w-0">
                <p className="text-base sm:text-xl font-semibold truncate">{formatNumber(totalMarketCap)}</p>
                <p className="text-xs text-muted-foreground">{t('totalMarketCap')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                <i className="fa-solid fa-chart-simple text-success text-sm sm:text-base"></i>
              </div>
              <div className="min-w-0">
                <p className="text-base sm:text-xl font-semibold truncate">{formatNumber(total24hVolume)}</p>
                <p className="text-xs text-muted-foreground">{t('volume24h')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-crypto-bitcoin/10 flex items-center justify-center shrink-0">
                <i className="fa-brands fa-bitcoin text-crypto-bitcoin text-sm sm:text-base"></i>
              </div>
              <div className="min-w-0">
                <p className="text-base sm:text-xl font-semibold">54.2%</p>
                <p className="text-xs text-muted-foreground">{t('btcDominance')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-warning/10 flex items-center justify-center shrink-0">
                <i className="fa-solid fa-fire text-warning text-sm sm:text-base"></i>
              </div>
              <div className="min-w-0">
                <p className="text-base sm:text-xl font-semibold">72</p>
                <p className="text-xs text-muted-foreground">{t('fearGreed')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Main Table */}
        <div className="xl:col-span-3 space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
              <Input
                placeholder={t('searchCoins')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Tabs value={category} onValueChange={setCategory} className="hidden sm:block">
                <TabsList className="h-9">
                  <TabsTrigger value="all" className="text-xs px-3">{t('all')}</TabsTrigger>
                  <TabsTrigger value="defi" className="text-xs px-3">{t('defi')}</TabsTrigger>
                  <TabsTrigger value="nft" className="text-xs px-3">{t('nft')}</TabsTrigger>
                  <TabsTrigger value="gaming" className="text-xs px-3">{t('gaming')}</TabsTrigger>
                </TabsList>
              </Tabs>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder={t('sortBy')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rank">{t('rank')}</SelectItem>
                  <SelectItem value="price">{t('price')}</SelectItem>
                  <SelectItem value="change">{t('change24h')}</SelectItem>
                  <SelectItem value="volume">{t('volume')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Market Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>{t('name')}</TableHead>
                      <TableHead className="text-right">{t('price')}</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">{t('change24h')}</TableHead>
                      <TableHead className="text-right hidden md:table-cell">{t('change7d')}</TableHead>
                      <TableHead className="text-right hidden lg:table-cell">{t('marketCap')}</TableHead>
                      <TableHead className="text-right hidden xl:table-cell">{t('volume')}</TableHead>
                      <TableHead className="w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((coin) => (
                      <TableRow key={coin.symbol} className="hover:bg-muted/50">
                        <TableCell className="font-medium text-muted-foreground">{coin.rank}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className={cn(
                              "w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shrink-0",
                              coin.symbol === 'BTC' ? 'bg-crypto-bitcoin/15' :
                              coin.symbol === 'ETH' ? 'bg-crypto-ethereum/15' : 'bg-primary/10'
                            )}>
                              <i className={cn(coin.icon, coin.color, "text-xs sm:text-sm")}></i>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{coin.name}</p>
                              <p className="text-xs text-muted-foreground">{coin.symbol}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          ${coin.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: coin.price < 1 ? 4 : 2 })}
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-mono text-sm hidden sm:table-cell",
                          coin.change24h >= 0 ? "text-success" : "text-destructive"
                        )}>
                          {coin.change24h >= 0 ? '+' : ''}{coin.change24h.toFixed(2)}%
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-mono text-sm hidden md:table-cell",
                          coin.change7d >= 0 ? "text-success" : "text-destructive"
                        )}>
                          {coin.change7d >= 0 ? '+' : ''}{coin.change7d.toFixed(2)}%
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm hidden lg:table-cell">
                          {formatNumber(coin.marketCap)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm hidden xl:table-cell">
                          {formatNumber(coin.volume)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-7 px-2">
                            <i className="fa-solid fa-star text-muted-foreground text-xs"></i>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Trending */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-fire text-warning"></i>
                <CardTitle className="text-base">{t('trending')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {trendingCoins.map((coin, index) => (
                <div key={coin.symbol} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-4">{index + 1}</span>
                    <div className={cn("w-7 h-7 rounded-full flex items-center justify-center", `bg-${coin.color.split('-')[1]}-500/15`)}>
                      <i className={cn(coin.icon, coin.color, "text-xs")}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{coin.symbol}</p>
                      <p className="text-xs text-muted-foreground">{coin.name}</p>
                    </div>
                  </div>
                  <Badge variant="gain" className="text-xs">
                    +{coin.change.toFixed(1)}%
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Top Gainers */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-arrow-trend-up text-success"></i>
                <CardTitle className="text-base">{t('topGainers')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {marketData.filter(c => c.change24h > 0).sort((a, b) => b.change24h - a.change24h).slice(0, 3).map((coin) => (
                <div key={coin.symbol} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <div className={cn("w-7 h-7 rounded-full flex items-center justify-center bg-primary/10")}>
                      <i className={cn(coin.icon, coin.color, "text-xs")}></i>
                    </div>
                    <span className="text-sm font-medium">{coin.symbol}</span>
                  </div>
                  <span className="text-sm text-success font-mono">+{coin.change24h.toFixed(2)}%</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Top Losers */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-arrow-trend-down text-destructive"></i>
                <CardTitle className="text-base">{t('topLosers')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {marketData.filter(c => c.change24h < 0).sort((a, b) => a.change24h - b.change24h).slice(0, 3).map((coin) => (
                <div key={coin.symbol} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-2">
                    <div className={cn("w-7 h-7 rounded-full flex items-center justify-center bg-primary/10")}>
                      <i className={cn(coin.icon, coin.color, "text-xs")}></i>
                    </div>
                    <span className="text-sm font-medium">{coin.symbol}</span>
                  </div>
                  <span className="text-sm text-destructive font-mono">{coin.change24h.toFixed(2)}%</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Markets;
